/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.5.0 - useRecurringCampaigns Hook (REAL API)
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file useRecurringCampaigns.ts
 * @description Hook para gestión de campañas recurrentes (conectado a backend real)
 * @version 2.0.0
 * @date 2025-11-27
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useUserContext } from '../layouts/UserContext';


// ═══════════════════════════════════════════════════════════════════════════
// API CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/wasp';
const API_KEY = 'anubis-whatsapp-api-key';

const getAuthToken = (): string => {
  try {
    const token = localStorage.getItem('hatateToken');
    return token || '';
  } catch (e) {
    console.error('Error getting auth token:', e);
    return '';
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

// Importar tipos existentes
import type { 
  RecurrenceConfig,
  CampaignStatus,
  RecurringCampaign
} from '../types/scheduler.types';

// Re-exportar para compatibilidad
export type { RecurrenceConfig, CampaignStatus, RecurringCampaign } from '../types/scheduler.types';

export interface CampaignFilters {
  status?: CampaignStatus;
  channel?: string;
}

export interface UseRecurringCampaignsReturn {
  // Data
  campaigns: RecurringCampaign[];
  activeCampaigns: RecurringCampaign[];
  pausedCampaigns: RecurringCampaign[];
  completedCampaigns: RecurringCampaign[];
  
  // Loading states
  isLoading: boolean;
  isSyncing: boolean; // NUEVO: para refresh silencioso
  error: string | null;
  lastSync: Date | null; // NUEVO: última sincronización
  
  // Filters
  filters: CampaignFilters;
  setFilters: (filters: CampaignFilters) => void;
  
  // CRUD Operations
  createCampaign: (data: Partial<RecurringCampaign>) => Promise<RecurringCampaign>;
  updateCampaign: (id: string, data: Partial<RecurringCampaign>) => Promise<RecurringCampaign>;
  deleteCampaign: (id: string) => Promise<void>;
  getCampaign: (id: string) => Promise<RecurringCampaign | null>;
  
  // Campaign actions
  pauseCampaign: (id: string) => Promise<void>;
  resumeCampaign: (id: string) => Promise<void>;
  
  // Executions
  getCampaignExecutions: (id: string, limit?: number, offset?: number) => Promise<any[]>;
  
  // Helpers
  getCampaignsByStatus: (status: CampaignStatus) => RecurringCampaign[];
  
  // Actions
  refresh: () => Promise<void>;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export const useRecurringCampaigns = (): UseRecurringCampaignsReturn => {
  // ═══════════════════════════════════════════════════════════════════════
  // CONTEXT
  // ═══════════════════════════════════════════════════════════════════════
  
  const { user } = useUserContext();
  const phoneId = user?._id || '';
  
  // ═══════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════
  
  const [campaigns, setCampaigns] = useState<RecurringCampaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CampaignFilters>({});
  const [lastSync, setLastSync] = useState<Date | null>(null); 

  // ═══════════════════════════════════════════════════════════════════════
  // COMPUTED VALUES
  // ═══════════════════════════════════════════════════════════════════════
  
  const activeCampaigns = useMemo(() => 
    campaigns.filter(c => c.status === 'active'),
    [campaigns]
  );
  
  const pausedCampaigns = useMemo(() => 
    campaigns.filter(c => c.status === 'paused'),
    [campaigns]
  );
  
  const completedCampaigns = useMemo(() => 
    campaigns.filter(c => c.status === 'completed'),
    [campaigns]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // LOAD CAMPAIGNS
  // ═══════════════════════════════════════════════════════════════════════
  
  const loadCampaigns = useCallback(async (silent = false) => {
    try {
      // Solo mostrar loading spinner en la primera carga
      if (!silent) {
        setIsLoading(true);
      } else {
        setIsSyncing(true);
      }
      
      setError(null);
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      if (!silent) {
        console.log('📡 Cargando campaigns desde API...');
        console.log('  - phoneId:', phoneId);
      }
      
      const response = await axios.get(`${API_BASE_URL}/scheduler/campaigns`, {
        params: { 
          phoneId,
          ...filters 
        },
        headers: {
          'x-api-key': API_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!silent) {
        console.log('✅ Campaigns cargadas:', response.data);
      }
      
      if (response.data.success) {
        setCampaigns(response.data.campaigns || []);
        setLastSync(new Date());
      } else {
        throw new Error(response.data.message || 'Error al cargar campaigns');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al cargar campañas recurrentes';
      
      // Solo mostrar error si no es refresh silencioso
      if (!silent) {
        setError(message);
        console.error('❌ Error loading campaigns:', err);
        console.error('  - Status:', err.response?.status);
        console.error('  - Data:', err.response?.data);
      }
    } finally {
      setIsLoading(false);
      setIsSyncing(false);
    }
  }, [filters, phoneId]);

  // ═══════════════════════════════════════════════════════════════════════
  // CREATE CAMPAIGN
  // ═══════════════════════════════════════════════════════════════════════
  
  const createCampaign = useCallback(async (
    data: Partial<RecurringCampaign>
  ): Promise<RecurringCampaign> => {
    try {
      setError(null);
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      console.log('📡 Creando campaign en API...');
      console.log('  - phoneId:', phoneId);
      console.log('  - Data:', data);
      
      const response = await axios.post(
        `${API_BASE_URL}/scheduler/campaigns`,
        {
          ...data,
          phoneId
        },
        {
          headers: {
            'x-api-key': API_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ Campaign creada:', response.data);
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Error al crear campaña');
      }

      // Refresh local
      await loadCampaigns(true);
      
      // Disparar evento global para que otros componentes se enteren
      window.dispatchEvent(new CustomEvent('campaignCreated', { 
        detail: response.data.campaign 
      }));
      
      return response.data.campaign;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al crear campaña';
      setError(message);
      console.error('❌ Error creating campaign:', err);
      throw err;
    }
  }, [phoneId, loadCampaigns]);

  // ═══════════════════════════════════════════════════════════════════════
  // UPDATE CAMPAIGN
  // ═══════════════════════════════════════════════════════════════════════
  
  const updateCampaign = useCallback(async (
    id: string,
    data: Partial<RecurringCampaign>
  ): Promise<RecurringCampaign> => {
    try {
      setError(null);
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      console.log('📡 Actualizando campaign en API...');
      
      const response = await axios.put(
        `${API_BASE_URL}/scheduler/campaigns/${id}`,
        data,
        {
          params: { phoneId },
          headers: {
            'x-api-key': API_KEY,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ Campaign actualizada:', response.data);
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Error al actualizar campaña');
      }

      await loadCampaigns(true);
      
      return response.data.campaign;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al actualizar campaña';
      setError(message);
      console.error('❌ Error updating campaign:', err);
      throw err;
    }
  }, [phoneId, loadCampaigns]);

  // ═══════════════════════════════════════════════════════════════════════
  // DELETE CAMPAIGN
  // ═══════════════════════════════════════════════════════════════════════
  
  const deleteCampaign = useCallback(async (id: string): Promise<void> => {
    try {
      setError(null);
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      console.log('📡 Eliminando campaign en API...');
      
      const response = await axios.delete(
        `${API_BASE_URL}/scheduler/campaigns/${id}`,
        {
          params: { phoneId },
          headers: {
            'x-api-key': API_KEY,
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      console.log('✅ Campaign eliminada:', response.data);
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Error al eliminar campaña');
      }

      await loadCampaigns(true);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al eliminar campaña';
      setError(message);
      console.error('❌ Error deleting campaign:', err);
      throw err;
    }
  }, [phoneId, loadCampaigns]);

  // ═══════════════════════════════════════════════════════════════════════
  // GET CAMPAIGN
  // ═══════════════════════════════════════════════════════════════════════
  
  const getCampaign = useCallback(async (id: string): Promise<RecurringCampaign | null> => {
    try {
      setError(null);
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      const response = await axios.get(
        `${API_BASE_URL}/scheduler/campaigns/${id}`,
        {
          params: { phoneId },
          headers: {
            'x-api-key': API_KEY,
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        return response.data.campaign;
      }
      
      return null;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al obtener campaña';
      setError(message);
      console.error('❌ Error getting campaign:', err);
      return null;
    }
  }, [phoneId]);

  // ═══════════════════════════════════════════════════════════════════════
  // PAUSE CAMPAIGN
  // ═══════════════════════════════════════════════════════════════════════
  
  const pauseCampaign = useCallback(async (id: string): Promise<void> => {
    try {
      setError(null);
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      console.log('📡 Pausando campaign en API...');
      
      const response = await axios.post(
        `${API_BASE_URL}/scheduler/campaigns/${id}/pause`,
        {},
        {
          params: { phoneId },
          headers: {
            'x-api-key': API_KEY,
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      console.log('✅ Campaign pausada:', response.data);
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Error al pausar campaña');
      }

      await loadCampaigns(true);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al pausar campaña';
      setError(message);
      console.error('❌ Error pausing campaign:', err);
      throw err;
    }
  }, [phoneId, loadCampaigns]);

  // ═══════════════════════════════════════════════════════════════════════
  // RESUME CAMPAIGN
  // ═══════════════════════════════════════════════════════════════════════
  
  const resumeCampaign = useCallback(async (id: string): Promise<void> => {
    try {
      setError(null);
      
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      console.log('📡 Reanudando campaign en API...');
      
      const response = await axios.post(
        `${API_BASE_URL}/scheduler/campaigns/${id}/resume`,
        {},
        {
          params: { phoneId },
          headers: {
            'x-api-key': API_KEY,
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      console.log('✅ Campaign reanudada:', response.data);
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Error al reanudar campaña');
      }

      await loadCampaigns(true);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Error al reanudar campaña';
      setError(message);
      console.error('❌ Error resuming campaign:', err);
      throw err;
    }
  }, [phoneId, loadCampaigns]);

  // ═══════════════════════════════════════════════════════════════════════
  // GET CAMPAIGN EXECUTIONS
  // ═══════════════════════════════════════════════════════════════════════
  
  const getCampaignExecutions = useCallback(async (
    id: string,
    limit: number = 10,
    offset: number = 0
  ): Promise<any[]> => {
    try {
      const token = getAuthToken();
      
      if (!phoneId) {
        throw new Error('Usuario no autenticado. Por favor inicia sesión nuevamente.');
      }
      
      const response = await axios.get(
        `${API_BASE_URL}/scheduler/campaigns/${id}/executions`,
        {
          params: { phoneId, limit, offset },
          headers: {
            'x-api-key': API_KEY,
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        return response.data.executions || [];
      }
      
      return [];
    } catch (err: any) {
      console.error('❌ Error getting campaign executions:', err);
      return [];
    }
  }, [phoneId]);

  // ═══════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════
  
  const getCampaignsByStatus = useCallback((status: CampaignStatus): RecurringCampaign[] => {
    return campaigns.filter(c => c.status === status);
  }, [campaigns]);

  // ═══════════════════════════════════════════════════════════════════════
  // REFRESH
  // ═══════════════════════════════════════════════════════════════════════
  
  const refresh = useCallback(async () => {
    await loadCampaigns();
  }, [loadCampaigns]);

  // ═══════════════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════════════
  
  // Load initial data
  useEffect(() => {
    loadCampaigns(false); // Primera carga con loading spinner
  }, [filters, phoneId]); // Recargar cuando cambien filtros


  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadCampaigns(true); // Refresh silencioso en background
    }, 60000);

    return () => clearInterval(interval);
  }, [loadCampaigns]);

  // Escuchar cuando se crea una campaña desde otro componente
  useEffect(() => {
    const handleCampaignCreated = () => {
      console.log('🔔 Evento campaignCreated detectado, refrescando...');
      loadCampaigns(true);
    };

    window.addEventListener('campaignCreated', handleCampaignCreated);
    
    return () => {
      window.removeEventListener('campaignCreated', handleCampaignCreated);
    };
  }, [loadCampaigns]);


  // ═══════════════════════════════════════════════════════════════════════
  // RETURN
  // ═══════════════════════════════════════════════════════════════════════
  
  return {
    // Data
    campaigns,
    activeCampaigns,
    pausedCampaigns,
    completedCampaigns,
    
    // State
    isLoading,
    error,
    isSyncing,
    lastSync,

    // Filters
    filters,
    setFilters,
    
    // CRUD
    createCampaign,
    updateCampaign,
    deleteCampaign,
    getCampaign,
    
    // Campaign actions
    pauseCampaign,
    resumeCampaign,
    
    // Executions
    getCampaignExecutions,
    
    // Helpers
    getCampaignsByStatus,
    
    // Actions
    //refresh
    refresh: () => loadCampaigns(false)
  };
};

export default useRecurringCampaigns;